import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ConfirmCancelEnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  userFullName: string;
  onConfirm: (reason?: string) => Promise<void>;
}

function ConfirmCancelEnrollmentModal({
  isOpen,
  onClose,
  userFullName,
  onConfirm,
}: ConfirmCancelEnrollmentModalProps) {
  const [isWorking, setIsWorking] = useState(false);
  const [reason, setReason] = useState("");

  const handleClose = () => {
    setReason("");
    onClose();
  };

  const handleConfirm = async () => {
    setIsWorking(true);
    try {
      await onConfirm(reason.trim() || undefined);
      setReason("");
      onClose();
    } catch (error) {
      console.error("Failed to cancel enrollment:", error);
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Dialog onOpenChange={(open) => !open && handleClose()} open={isOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Cancel Enrollment</DialogTitle>
          <DialogDescription className="text-gray-600 dark:text-gray-400">
            Are you sure you want to cancel the enrollment for{" "}
            <strong>{userFullName}</strong>?
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <Label htmlFor="cancellationReason">
            Cancellation Reason (Optional)
          </Label>
          <Textarea
            className="mt-2"
            disabled={isWorking}
            id="cancellationReason"
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for cancellation..."
            rows={3}
            value={reason}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            This will be saved for audit purposes.
          </p>
        </div>

        <DialogFooter>
          <Button disabled={isWorking} onClick={handleClose} variant="outline">
            Keep Enrollment
          </Button>
          <Button
            disabled={isWorking}
            onClick={handleConfirm}
            variant="destructive"
          >
            {isWorking ? "Cancelling..." : "Cancel Enrollment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmCancelEnrollmentModal;
